import type { ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useI18n } from '../i18n/index.js';
import styles from '../routes/pageStyles.module.css';

/**
 * 끌어서 차례를 바꿀 수 있는 주제 줄.
 *
 * 주제는 **색으로 구분된다.** 차례가 제멋대로면 매번 어느 색이 무엇인지 다시
 * 찾아야 한다. 자주 쓰는 것을 앞에 두게 해 준다.
 *
 * **길게 눌러야 시작된다.** 칩은 누르면 수정 화면이 열리는 버튼이라, 손가락이
 * 닿자마자 끌리면 스크롤도 탭도 안 된다(픽 격자와 같은 규칙).
 */
export function SortableTopicRow({
  order,
  onReorder,
  children,
}: {
  /** 차례대로의 topicId. 이 배열이 곧 화면 차례다 */
  order: string[];
  onReorder: (next: string[]) => void;
  children: ReactNode;
}) {
  const { t } = useI18n();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    /*
     * ⚠️ **Enter는 넘겨주지 않는다.** dnd-kit의 기본값은 Space와 Enter 둘 다
     * 집기로 쓰는데, 칩 자체가 누르면 열리는 버튼이라 Enter까지 가져가면
     * 키보드로는 주제를 열 수가 없다.
     */
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space'] },
    }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent): void => {
    if (over === null || active.id === over.id) {
      return;
    }
    const from = order.indexOf(String(active.id));
    const to = order.indexOf(String(over.id));
    if (from === -1 || to === -1) {
      return;
    }
    onReorder(arrayMove(order, from, to));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={order} strategy={horizontalListSortingStrategy}>
        <ul className={styles.topicRow}>
          {children}
          {/* 화면에 안 보이는 안내 — 키보드로 오는 사람에게만 필요하다 */}
          <li className="srOnly">{t('timeline.topicReorderHint')}</li>
        </ul>
      </SortableContext>
    </DndContext>
  );
}

/** 끌 수 있는 주제 칩 하나 */
export function SortableTopicChip({ topicId, children }: { topicId: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: topicId,
  });

  return (
    <li
      ref={setNodeRef}
      className={styles.topicSlot}
      data-dragging={isDragging || undefined}
      style={{
        /*
         * ⚠️ `CSS.Translate`다. `CSS.Transform`은 `scaleX`/`scaleY`까지 담아
         * 오는데, 칩마다 글자 수가 달라 폭이 제각각이라 그대로 쓰면 끌 때마다
         * 글씨가 늘어났다 줄었다 한다(타임라인에서 이미 겪었다).
         */
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </li>
  );
}
